import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import ModalActions from 'reducers/Modal'
import { Pill, Button, Tonality, Modal } from '@dsny/dsny-component-library'
import { Personadd, Close } from '@dsny/dsny-component-library/icons'
import { useMobileMediaQuery } from 'utils/hooks/useMediaQueries'
import { trackEvent } from 'utils/segmentService'
import useLocalStorage from 'utils/hooks/useLocalStorage'
import InviteList from './InviteList'
import {
  XButtonWrapper,
  ButtonWrapper,
  TitleWrapper,
  ModalWrapper,
  IconWrapper,
  TermsWrapper,
  TermsContainer
} from './InviteConnection.styles'

const InviteConnection = () => {
  const { t } = useTranslation()
  const theme = useTheme()
  const dispatch = useDispatch()
  const isMobile = useMobileMediaQuery()
  const [error, setError] = useState(false)
  const [isTermsChecked, setIsTermsChecked] = useState(false)
  const [inviteModalDismissed, setInviteModalDismissed] = useLocalStorage(
    'invite-modal-dismissed',
    false
  )
  const hasDismissedInviteModal = Boolean(inviteModalDismissed)
  const showInviteModal = useSelector((state) => state.modal?.showInviteModal)
  const showInviteListModal = useSelector(
    (state) => state.modal?.showInviteListModal
  )

  const trackInviteConnectionClick = (type) => {
    trackEvent('Click', {
      source: 'invite_connection',
      type
    })
  }

  const toggleInviteModal = () => {
    setIsTermsChecked(false)
    setError(false)

    if (hasDismissedInviteModal) {
      if (showInviteModal) {
        dispatch(ModalActions.toggleInviteModal())
      }
      if (!showInviteListModal) {
        dispatch(ModalActions.toggleInviteListModal())
      }
      return
    }

    dispatch(ModalActions.toggleInviteModal())
  }

  const handleCheckboxChange = (e) => {
    setIsTermsChecked(!isTermsChecked)
    if (e.target.checked) {
      setError(false)
    }
  }

  const handleCancelButtonClick = () => {
    trackInviteConnectionClick('close')
    dispatch(ModalActions.toggleInviteModal())
    setError(false)
  }

  const handleStartInviting = () => {
    if (!isTermsChecked) {
      setError(true)
      return
    }

    trackInviteConnectionClick('start_inviting')
    setInviteModalDismissed(true)
    dispatch(ModalActions.toggleInviteModal())
    dispatch(ModalActions.toggleInviteListModal())
  }

  useEffect(() => {
    if (showInviteModal && hasDismissedInviteModal) {
      dispatch(ModalActions.toggleInviteModal())
      if (!showInviteListModal) {
        dispatch(ModalActions.toggleInviteListModal())
      }
    }
  }, [
    dispatch,
    hasDismissedInviteModal,
    showInviteListModal,
    showInviteModal
  ])

  const renderIcon = () => {
    if (isMobile) {
      return (
        <Personadd
          width={18}
          color={theme.colors.neutralW90}
          onClick={toggleInviteModal} />
      )
    }

    return (
      <Pill
        variant="outlined"
        mode="dark"
        label={t('invite-connection.invite-a-connection')}
        iconPosition="leading"
        icon={<Personadd width={14} color={theme.colors.neutralW90} />}
        onClick={toggleInviteModal} />
    )
  }

  return (
    <>
      <IconWrapper $isMobile={isMobile}>{renderIcon()}</IconWrapper>

      <Modal
        isOpen={showInviteModal}
        analyticsModalType="invite_connection"
        trackEvent={trackEvent}
        containerStyles={{
          padding: isMobile ? '24px 16px' : '24px 64px 40px 64px',
          background: theme.colors.neutralW20,
          border: `1px solid ${theme.colors.neutralW30}`,
          width: isMobile ? '100%' : 676
        }}
        onClose={handleCancelButtonClick}>
        <XButtonWrapper onClick={handleCancelButtonClick}>
          <Close width={14} color={theme.colors.neutralW80} />
        </XButtonWrapper>

        <ModalWrapper $isMobile={isMobile}>
          <TitleWrapper $isMobile={isMobile}>
            <Tonality.UIHeading
              headingStyle="headline5"
              color={theme.colors.neutralW90}
              style={{ marginBottom: 8 }}>
              {t('invite-connection.invite-title')}
            </Tonality.UIHeading>
            <Tonality.UIText textStyle="label" color={theme.colors.neutralW70}>
              {t('invite-connection.invite-desc')}
            </Tonality.UIText>
          </TitleWrapper>

          <TermsWrapper $isMobile={isMobile}>
            <TermsContainer>
              <Tonality.Checkbox
                checked={isTermsChecked}
                mode="dark"
                wrapperStyle={{ paddingTop: 3 }}
                style={{
                  backgroundColor: 'transparent',
                  borderColor: theme.colors.neutralW90
                }}
                onClick={handleCheckboxChange} />
              <Tonality.UIText
                textStyle="label"
                color={theme.colors.neutralW90}>
                {t('invite-connection.terms-disclaimer')}
              </Tonality.UIText>
            </TermsContainer>
            {error && (
              <Tonality.UIText
                textStyle="caption"
                color={theme.colors.error70}
                style={{ marginLeft: 24, marginTop: 8 }}>
                {t('invite-connection.terms-error')}
              </Tonality.UIText>
            )}
          </TermsWrapper>

          <ButtonWrapper $isMobile={isMobile}>
            <Button
              variant="primary"
              setting="player"
              mode="dark"
              width="100%"
              onClick={handleStartInviting}>
              {t('invite-connection.start-inviting')}
            </Button>
          </ButtonWrapper>
        </ModalWrapper>
      </Modal>

      {showInviteListModal && <InviteList />}
    </>
  )
}

export default InviteConnection
